/* eslint-disable */
import * as React from 'react';
import { HocOptions } from '../common/generic-hoc.types';
import { StylesProps } from '../../theme/jss-types';
import { consignmentDetailsIntStyles } from './consignment-details.styles';
import GenericHoc from '../common/generic-hoc';
import { snakeToPretty } from 'library/lodash-helper';
import {
    Button,
    Image,
    Tooltip,
    Collapse,
} from 'antd';
import { DetailsToShowKeysDomestic, DetailsToShowKeysIntl } from './consignment-details.constants';
import { useTranslation } from 'react-i18next';
import DocumentDetailsModal from './document-details-modal';
import {
    InfoCircleOutlined, ClockCircleTwoTone, EnvironmentFilled,
} from '@ant-design/icons';
import TableModal from '../table-modal/tableModal';
import { RouterProps } from 'library/Types';
import { PAGE_NAMES } from 'types/page.types';
import { ReduxStore } from 'reducers/redux.types';
import { ViewData } from 'types/view.types';
import { isEmpty } from 'lodash';
import StatusPrettyName from './status-pretty-name';
import ContentFilledIcon from 'assets/contents-filled';
import useIsMobile from 'hooks/useIsMobile';
import { formatDateTime, getFieldPrettyName } from './consignments.helper';
import moment from 'moment';

interface GroupedColumnDetail {
    column_id?: string;
    header?: string;
    columns?: Array<{
        pretty_name: string;
        id: string;
        key: string;
        type: string;
    }>;
}

interface GroupedColumnData {
    tab_header: string;
    details: GroupedColumnDetail[];
}

interface ConsignmentDetailedInternationalProps extends StylesProps<ReturnType<typeof consignmentDetailsIntStyles>>,
    RouterProps {
    consignment: Record<string, any>;
    consignmentSectionList: ViewData[][];
    separateChildCN: boolean;
    selectedTab: string;
    expandPage: boolean;
    currencySymbol: string;
    completeCustomFieldsList: any;
    consignment_details_column_list?: GroupedColumnData[];
    showPieceAsItem?: boolean;
}

const ConsignmentDetailedInternational = (props: ConsignmentDetailedInternationalProps) => {
    const {
        classes,
        consignment,
        consignmentSectionList,
        separateChildCN,
        location,
        selectedTab,
        expandPage,
        currencySymbol,
        completeCustomFieldsList,
        consignment_details_column_list,
        // client requirement, show piece as item
        showPieceAsItem,
    } = props;

    const [imageUrl, setImageUrl] = React.useState<string | null>(null);
    const { t, i18n } = useTranslation();
    const [isVisible, setIsVisible] = React.useState<boolean>(false);
    const [modalRow, setModalRow] = React.useState<any>(null);
    const [isPriceDetailsModalVisible, setIsPriceDetailsModalVisible] = React.useState<boolean>(false);
    const [priceDataList, setPriceDataList] = React.useState<any>([]);
    // eslint-disable-next-line no-spaced-func
    const [viewDataKeyList, setViewDataKeyList] = React.useState<Record<string, (string | undefined)[]>>({});
    const [viewDataKeyLabelMap, setViewDataKeyLabelMap] = React.useState<Record<string, string>>({});
    const [currentPieceNumber, setCurrentPieceNumber] = React.useState(0);
    const piecesArray = consignment?.pieces_detail;
    const isMobile = useIsMobile();
    
    // Map selectedTab to tab_header in consignment_details_column_list
    const getTabHeaderFromSelectedTab = (tab: string): string | null => {
        const tabMapping: Record<string, string> = {
            'primary_details': 'Primary Details',
            'pieces_details': 'Piece Details',
            'address_details': 'Address Details',
        };
        return tabMapping[tab] || null;
    };

    const getDetailsToShowKeyList = () => {
        const baseKeys = consignment?.is_international ? DetailsToShowKeysIntl : DetailsToShowKeysDomestic;

        // Filter based on consignment_details_column_list from backend
        if (Array.isArray(consignment_details_column_list) && consignment_details_column_list.length) {
            const tabHeader = getTabHeaderFromSelectedTab(selectedTab);
            
            // Find the tab object matching the current selectedTab
            const currentTabConfig = tabHeader 
                ? consignment_details_column_list.find(
                    (tab) => tab.tab_header === tabHeader
                  )
                : null;

            // If we found the tab config, use its column_ids in the specified order
            if (currentTabConfig) {
                const allowedKeys = currentTabConfig.details
                    .map((detail) => detail.column_id)
                    .filter((key): key is string => key !== undefined);

                // Filter and reorder each section's keys based on the tab config
                const filteredKeys: Record<string, (string | undefined)[]> = {};
                Object.keys(baseKeys).forEach((section) => {
                    const sectionKeys = baseKeys[section].filter(
                        (key) => key !== undefined && allowedKeys.includes(key)
                    );
                    
                    // Sort by the order in allowedKeys
                    sectionKeys.sort((a, b) => {
                        const indexA = allowedKeys.indexOf(a as string);
                        const indexB = allowedKeys.indexOf(b as string);
                        return indexA - indexB;
                    });
                    
                    filteredKeys[section] = sectionKeys;
                });
                return filteredKeys;
            }
        }

        // View-based filtering (existing logic)
        if (!isEmpty(viewDataKeyList)) return viewDataKeyList;

        return baseKeys;
    };

    React.useEffect(() => {
        const viewId = new URLSearchParams(location.search)?.get('view');

        if (viewId) {
            const detailsToShowKeysList: Record<string, (string | undefined)[]> = getDetailsToShowKeyList();
            const viewData = consignmentSectionList?.flat().find((view) => view.id === viewId);
            const consignmentFieldList: Record<string, string> = viewData?.metadata?.fieldList?.reduce(
                (obj: Record<string, string>, item) => {
                    if (item?.enable) {
                        return { ...obj, [item.id]: item.name };
                    }
                    return obj;
                }, {},
            ) || {};
            const viewDataKeys: Record<string, (string | undefined)[]> = {};

            if (!isEmpty(consignmentFieldList)) {
                Object.keys(detailsToShowKeysList).forEach((key) => {
                    viewDataKeys[key] = detailsToShowKeysList[key]?.filter(
                        (item) => item && consignmentFieldList?.[item],
                    ) || [];
                });
                setViewDataKeyList(viewDataKeys);
                setViewDataKeyLabelMap(consignmentFieldList);
            }
        }
    }, [consignmentSectionList]);

    const labelValuePair = (key: string) => {
        const data = {
            label: getFieldPrettyName(key, t, i18n, viewDataKeyLabelMap),
            value: consignment[key],
        };

        // Apply value transformations only (label is already handled by getFieldPrettyName)
        switch (key) {
            case 'is_risk_surcharge_applicable':
                data.value = data.value !== null && data.value !== undefined ? data.value.toString() : '';
                break;
            case 'length':
            case 'width':
            case 'height':
                data.value = data.value ? `${consignment[key]}  cm` : '';
                break;
            case 'weight':
                data.value = data.value ? `${consignment[key]}  Kg` : '';
                break;
            case 'given_length':
            case 'given_width':
            case 'given_height':
                data.value = data.value ? `${consignment[key]}  cm` : '';
                break;
            case 'given_weight':
                data.value = data.value ? `${consignment[key]}  Kg` : '';
                break;
            case 'fob_value':
            case 'freight_cost':
                data.value = consignment[key]
                    ? `${consignment[key]}  ${consignment[`${key}_currency`] || ''}`
                    : '';
                break;
            case 'total_gst_paid_amount':
                data.value = consignment[key]
                    ? `${consignment[key]}  ${consignment.total_gst_paid_currency || ''}`
                    : '';
                break;
            case 'consignor_kyc_front_image':
            case 'consignor_kyc_back_image':
                if (data.value) {
                    data.value = (
                        <Button
                            type="link"
                            onClick={() => {
                                setImageUrl(consignment[key]);
                            }}
                        >
                            View
                        </Button>
                    );
                }
                break;
            case 'against_bond_lut':
                if (data.value === 'B') data.value = 'Against Bond';
                else if (data.value === 'U') data.value = 'Undertaking';
                break;
            case 'is_stackable':
                if (!data.value) data.label = '';
                data.value = data.value ? data.value.toString() : 'false';
                break;
            case 'is_dangerous_good':
                if (!data.value) data.label = '';
                data.value = data.value ? data.value.toString() : 'false';
                break;
            case 'dangerous_good_accessible':
                if (!data.value) data.label = '';
                data.value = data.value ? data.value.toString() : 'false';
                break;
            case 'dangerous_good_un_number':
            case 'dangerous_good_name':
            case 'dangerous_good_class':
            case 'dangerous_good_division':
            case 'dangerous_good_category':
            case 'dangerous_good_packing_group':
            case 'dangerous_good_category_type':
            case 'dangerous_good_category_packing_instruction':
            case 'forward_reference_number':
            case 'reverse_reference_number':
            case 'igst':
            case 'cgst':
            case 'sgst':
            case 'tax_details_receiver_gstin':
            case 'tax_details_sender_gstin':
            case 'total_tax':
            case 'import_reference_number':
            case 'export_reference_number':
                if (!data.value) data.label = '';
                break;
            case 'payment_collection_mode_list':
                if (!data?.value?.length) data.label = '';
                data.value = data?.value?.length ? data.value?.map((mode : string) => mode).join(', ') : '';
                break;
            case 'delivery_time_slot_start':
            case 'delivery_time_slot_end':
                data.value = consignment[key]
                    ? formatDateTime(consignment[key], 'datetime', 'DD MMM YYYY, HH:mm A') : 'N/A';
                break;
            case 'expected_delivery_date':
                data.value = consignment[key]
                    ? moment(consignment[key]).format('DD MMM YYYY')
                    : 'N/A';
                break;
            default:
                break;
        }

        if (data.value !== false && !data.value) {
            data.value = 'N/A';
        }
        return data;
    };

    const renderLinkModal = (row: any) => {
        return (
            <DocumentDetailsModal
                isVisible={isVisible}
                onModalClose={() => setIsVisible(false)}
                documentDetails={row?.document_details}
            />
        );
    };
    const renderDocumentList = () => {
        return (
            <div className={isMobile ? classes.documentGroupMobile : classes.documentGroup}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>Document List :</div>
                <div>
                    <Button
                        type="link"
                        onClick={() => {
                            setModalRow(consignment);
                            setIsVisible(true);
                        }}
                        className={classes.documentViewButton}
                    >
                        VIEW
                    </Button>
                </div>
            </div>
        );
    };
    const renderCustomValue = (key: string) => {
        switch (key) {
            case 'document_list':
                return (
                    <>
                        {renderDocumentList()}
                        {renderLinkModal(modalRow)}
                    </>
                );
            default:
                return null;
        }
    };

    const priceDetailsModalColumns = [
        {
            column_id: 'name',
            pretty_name: 'Charge Name',
            width: 50,
        },
        {
            column_id: 'value',
            pretty_name: 'Charge Value',
            width: 50,
        },
    ];


    const handlePriceDetailsModal = (priceDetailsList: any[]) => {
        setIsPriceDetailsModalVisible(true);
        setPriceDataList(priceDetailsList);
    };

    const renderFinalPrice = (finalPrice: string, priceDetailsList: any[], label: string) => {
        return (
            <span>
                {label === 'Customer Charges'
                    ? `${currencySymbol} ${(finalPrice || 0)?.toString()}`
                    : (finalPrice || 0)?.toString()}
                <Tooltip title="">
                    <a>
                        <InfoCircleOutlined
                            style={{ marginLeft: 5 }}
                            onClick={() => handlePriceDetailsModal(priceDetailsList)}
                        />
                    </a>
                </Tooltip>
            </span>
        );
    };


    const renderPriceDetailsData = (label: string, priceDetails: any, width: string, chargeKey: string | undefined) => {
        let finalPriceValue = '';
        let priceDetailsList = [];
        if (Array.isArray(priceDetails) && priceDetails.length) {
            const finalPriceObject: any = priceDetails.find((price) => (price.id === 'finalPrice'));
            if (finalPriceObject) {
                finalPriceValue = finalPriceObject.value;
                priceDetailsList = priceDetails.filter((price) => (price.id !== 'finalPrice'));
            }
        }
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={chargeKey} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {finalPriceValue ? renderFinalPrice(finalPriceValue, priceDetailsList, label) : 'N/A'}
                </div>
            </span>
        );
    };

    const renderDeclaredValueField = (label: string, value: any, width: string, key: string | undefined) => {
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={key} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {(consignment.declared_currency || currencySymbol) ? `${consignment.declared_currency || currencySymbol} ${value}` : value}
                </div>
            </span>
        );
    };

    const renderField = (key: string | undefined, width: string) => {
        if (key === undefined) {
            return (
                <div className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} style={width ? { width } : undefined} />
            );
        }
        const { label, value } = labelValuePair(key);
        if (label === '') return null;
        if (key === 'document_list') {
            return renderCustomValue(key);
        }

        if (key === 'price_details') {
            return renderPriceDetailsData(label, value, width, key);
        }

        if (key === 'declared_value') {
            return renderDeclaredValueField(label, value, width, key);
        }
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={key} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {value}
                </div>
            </span>
        );
    };

    const renderPieceField = (idx: any, key: string|undefined) => {
        if (!key) return null;
        const data = {
            label: key,
            value: consignment.pieces_detail[idx][key] ?? 'N/A',
        };
        switch (key) {
            case 'length':
            case 'width':
            case 'height':
                if (data.value !== 'N/A') {
                    data.value = `${data.value} ${consignment.pieces_detail[idx]?.dimension_unit || 'cm'}`;
                }
                break;
            case 'declared_value':
                if (data.value !== 'N/A') {
                    data.value = `${data.value}  ${consignment.pieces_detail[idx]?.declared_currency || ''}`;
                }
                break;
            case 'weight':
                if (data.value !== 'N/A') {
                    data.value = `${data.value}  ${consignment.pieces_detail[idx]?.weight_unit || ''}`;
                }
                break;
            case 'reference_number': {
                const refNumber = consignment.pieces_detail[idx][key] || 'N/A';
                data.label = 'pieceReferenceNo';
                data.value = (
                    <Tooltip title={refNumber}>
                        {refNumber}
                    </Tooltip>
                );
                break;
            }
            case 'is_dangerous_good':
                data.value = (consignment.pieces_detail[idx][key]
                    && (consignment.pieces_detail[idx][key] === 'true' || consignment.pieces_detail[idx][key] === true))
                    ? 'true'
                    : 'false';
                data.label = consignment.pieces_detail[idx][key] !== null
                    && consignment.pieces_detail[idx][key] !== undefined
                    ? 'is_dangerous_good'
                    : '';
                break;
            case 'dangerous_good_accessible':
                data.value = (consignment.pieces_detail[idx][key]
                    && (consignment.pieces_detail[idx][key] === 'true' || consignment.pieces_detail[idx][key] === true))
                    ? 'true'
                    : 'false';
                data.label = consignment.pieces_detail[idx][key] !== null
                    && consignment.pieces_detail[idx][key] !== undefined
                    ? 'dangerous_good_accessible'
                    : '';
                break;
            case 'dangerous_good_un_number':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_UN_number' : '';
                break;
            case 'dangerous_good_name':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_name' : '';
                break;
            case 'dangerous_good_class':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_class' : '';
                break;
            case 'dangerous_good_division':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_division' : '';
                break;
            case 'dangerous_good_category':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category' : '';
                break;
            case 'dangerous_good_packing_group':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_packing_group' : '';
                break;
            case 'dangerous_good_category_type':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category_type' : '';
                break;
            case 'dangerous_good_category_packing_instruction':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category_packing_instruction' : '';
                break;
            case 'purchase_order_number':
                data.label = consignment.pieces_detail[idx][key] ? 'purchase_order_number' : '';
                break;
            case 'piece_product_code':
                data.label = consignment.pieces_detail[idx][key] ? 'product_code' : '';
                break;
            case 'manufacturing_country':
                data.label = consignment.pieces_detail[idx][key] ? 'manufacturing_country' : '';
                break;
            case 'is_kit':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.is_kit ? 'true' : 'false';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.is_kit !== null
                    && consignment.pieces_detail[idx]?.additional_properties?.is_kit !== undefined
                    ? 'is_kit'
                    : '';
                break;
            case 'eccn':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.eccn || 'N/A';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.eccn ? 'eccn' : '';
                break;
            case 'hsn_code':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.hsn_code || 'N/A';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.hsn_code ? 'hs_code' : '';
                break;
            case 'status':
                data.value = <StatusPrettyName status={consignment.pieces_detail[idx]?.status} />;
                data.label = consignment.pieces_detail[idx]?.status ? 'status' : '';
                break;
            case 'payment_collection_mode_list':
                data.label = data?.value !== 'N/A' ? data.label : '';
                data.value = data?.value !== 'N/A' ? data.value?.map((mode : string) => mode).join(', ') : '';
                break;
            default:
                break;
        }
        return (
            data.label !== '' ? (
                <div className={classes.fieldGroup} key={`field-${data.label}`}>
                    <div className={classes.fieldTitle}>
                        {i18n.exists(data.label) ? t(data.label) : snakeToPretty(data.label)}
                        {' : '}
                    </div>
                    <div className={classes.fieldValue}>
                        {data.value || 'N/A'}
                    </div>
                </div>
            ) : null
        );
    };

    const renderItemField = (item: any, key: string | undefined) => {
        if (!key) return <div className={classes.fieldGroup} key={key} style={{ width: '50%' }} />;
        let name = snakeToPretty(t(key));
        switch (key) {
            case 'duty_value': name = t('total_value');
                break;
            case 'item_value': name = t('item_rate');
                break;
            default:
                break;
        }

        const value = item[key] ?? 'N/A';

        return (
            <div className={classes.fieldGroup} key={key} style={{ width: '50%' }}>
                <div className={classes.fieldTitle}>
                    {name}
                    :
                </div>
                <div className={classes.fieldValue}>
                    {value}
                </div>
            </div>
        );
    };

    const renderCustomFields = (key: any) => {
        const consignmentCustomFields = Object.entries(consignment?.custom_fields || {});
        const customField = consignmentCustomFields?.find(
            (field: any) => Array.isArray(field) && field?.length === 2 && field[0] === key.id,
        );
        const label = key.prettyName;
        const value = customField ? customField[1] : 'N/A';

        const isValidUrl = (url: string) => {
            try {
                const urlObj = new URL(url);
                const allowedProtocols = ['http:', 'https:'];
                return allowedProtocols.includes(urlObj.protocol);
            } catch {
                return false;
            }
        };

        return (
            <span className={classes.fieldGroup} key={key} style={{ width: '50%' }}>
                <div className={classes.fieldTitle}>
                    {label}
                    :
                </div>
                <div className={classes.fieldValue}>
                    {(() => {
                        switch (key.dataType) {
                            case 'hyperlink':
                                if (!value || value === 'N/A' || typeof value !== 'string') {
                                    return 'N/A';
                                }

                                if (isValidUrl(value)) {
                                    return (
                                        <a
                                            href={value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#0066cc', textDecoration: 'underline' }}
                                        >
                                            Link
                                        </a>
                                    );
                                }

                                return (
                                    <Tooltip title={value}>
                                        <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                                            Invalid URL
                                        </span>
                                    </Tooltip>
                                );
                            case 'boolean':
                                return value === true || value === 'true' ? 'Yes' : 'No';
                            case 'date':
                            case 'datetime':
                                const dateValue = typeof value === 'number' ? value : (typeof value === 'string' ? parseInt(value, 10) : 0);
                                return formatDateTime(dateValue, key.dataType);
                            default:
                                return value;
                        }
                    })()}
                </div>
            </span>
        );
    };

    const renderEwbNumberField = () => {
        const ewayBillNumber = consignment?.ewb_number || 'N/A';
        return (
            <span className={classes.fieldGroup} style={{ width: '50%' }}>
                <div className={classes.fieldTitle}>
                    E-way Bill Number:
                </div>
                <div className={classes.fieldValue}>
                    {ewayBillNumber}
                </div>
            </span>
        );
    };

    const renderPrimaryDetailsTab = () => {
        const detailsToShow = getDetailsToShowKeyList();
        const {
            shipmentDetailsKeys,
            taxAndCustomDetailsKeys,
            dangerousGoodKeys,
            codDetailsKeys,
            billingDetailsKeys,
        } = detailsToShow;

        let packageDetailsKeys = detailsToShow?.packageDetailsKeys;

        if (separateChildCN) {
            const excludedKeys = ['given_length', 'given_height', 'given_width', 'length', 'height', 'width'];
            packageDetailsKeys = packageDetailsKeys.filter((key): key is string => key !== undefined && !excludedKeys.includes(key));
        }

        const existingDangerousGoodKeys = dangerousGoodKeys.filter((key): key is string => {
            if (!key) return false;
            const { label } = labelValuePair(key);
            return label !== undefined && label !== '' && label !== null;
        });

        return (
            // eslint-disable-next-line no-nested-ternary
            <div className={isMobile ? classes.detailsTabMobile : classes.detailsTab}>
                {shipmentDetailsKeys?.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('SHIPMENT_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {shipmentDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}
                {taxAndCustomDetailsKeys?.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('TAX_AND_CUSTOM_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {taxAndCustomDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}
                {packageDetailsKeys?.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('PACKAGE_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {packageDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}

                {codDetailsKeys?.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('COD_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {codDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}

                {billingDetailsKeys?.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('BILLING_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {billingDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}

                {existingDangerousGoodKeys.length ? (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('DANGEROUS_GOODS_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {existingDangerousGoodKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                ) : null}

                {Object.keys(completeCustomFieldsList)?.length > 0 || consignment?.ewb_number ? (
                    <>
                        <span className={classes.sectionHeading}>{t('OTHER_DETAILS')}</span>
                        <div className={classes.sectionBox}>
                            {consignment?.ewb_number && renderEwbNumberField()}
                            {(completeCustomFieldsList).map((key: any) => (
                                renderCustomFields(key)
                            ))}
                        </div>
                    </>
                ) : null}
            </div>
        );
    };

    const renderItemDetail = (item: any, idx: number) => {
        if (!item) return null;
        const keys = getDetailsToShowKeyList().itemDetails;
        return (
            <Collapse bordered={false} ghost>
                <Collapse.Panel
                    key={idx}
                    header={`${t('Item')} ${idx + 1}`}
                    className={classes.itemDetailsTab}
                >
                    <div className={classes.itemSectionBox}>
                        {keys.map((key: string|undefined) => (renderItemField(item, key)))}
                    </div>
                </Collapse.Panel>
            </Collapse>
        );
    };

    const renderPieceDetails = (piece: any, idx: number) => {
        const pieceKeys = getDetailsToShowKeyList().pieceDetails;
        return (
            <div>
                <span className={classes.sectionHeading}>{showPieceAsItem ? t('ITEMS_DETAILS') : t('PIECE_DETAILS')}</span>
                <div className={classes.sectionBox}>
                    {
                        pieceKeys.map((key) => renderPieceField(idx, key))
                    }
                </div>
                {
                    piece?.pieces_item_details ? (
                        <>
                            {piece.pieces_item_details.map((item: any, iidx: number) => renderItemDetail(item, iidx))}
                        </>
                    ) : null
                }
            </div>
        );
    };

    const renderPieceCard = (piece: any, idx: number) => {
        const dimensions = ['length', 'width', 'height'];
        const isSelected = currentPieceNumber === idx;

        return (
            <div
                className={classes.pieceCard}
                style={isSelected ? { borderColor: '#006EC3' } : { borderColor: '#DDDFE9' }}
                onClick={() => setCurrentPieceNumber(idx)}
            >
                <div>
                    {!separateChildCN ? (
                        <div className={classes.pieceCardUpper}>
                            <div className={classes.pieceCardLeft}>
                                <span className={classes.pieceHeader}>
                                    {'   '}
                                    <ContentFilledIcon style={{
                                        color: '#6A3DF5',
                                        backgroundColor: 'transparent',
                                    }}
                                    />
                                    {'   '}
                                    {`${showPieceAsItem ? t('Item') : t('Piece')} ${idx + 1}`}
                                </span>
                            </div>
                            <div className={classes.pieceCardRight}>
                                {piece?.pieces_item_details ? (
                                    <span>
                                        {piece?.pieces_item_details.length}
                                        {' ITEMS'}
                                    </span>
                                ) : null}
                                {piece.weight !== undefined && (
                                    <span>
                                        {piece.weight}
                                        {' '}
                                        {(consignment.pieces_detail[idx]?.weight_unit || '').toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={classes.pieceCardSCNS}>
                            <span className={classes.pieceHeader}>
                                {'   '}
                                <ContentFilledIcon style={{
                                    color: '#6A3DF5',
                                    backgroundColor: 'transparent',
                                }}
                                />
                                {'   '}
                                {piece.reference_number}
                            </span>
                            <div className={classes.pieceCardStatusLine}>
                                <span className={classes.pieceCardStatus}><StatusPrettyName status={piece.status} /></span>
                                {piece?.pieces_item_details ? (
                                    <span>
                                        {piece?.pieces_item_details.length}
                                        {' ITEMS •'}
                                    </span>
                                ) : null}

                                {piece.weight !== undefined && (
                                    <span>
                                        {piece.weight}
                                        {' '}
                                        {(consignment.pieces_detail[idx]?.weight_unit || '').toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <>
                    {(piece.length && piece.width && piece.height) > 0 && (
                        <div className={classes.pieceDimensions}>
                            <>
                                {dimensions.map((dim, i) => (
                                    piece[dim] != null && (
                                        <span key={dim}>
                                            {piece[dim]}
                                            {i < dimensions.length - 1
                                            && dimensions.some((d) => d !== dim && piece[d] != null) ? ' x ' : ''}
                                        </span>
                                    )
                                ))}
                                {dimensions.some((dim) => piece[dim] != null)
                                && ` ${(consignment.pieces_detail[idx]?.dimension_unit || 'cm').toUpperCase()}`}
                            </>
                        </div>
                    )}
                    <span className={classes.pieceDescription}>{piece.description}</span>
                </>
            </div>
        );
    };

    const renderPiecesDetailsTab = () => {
        return (
            <div className={classes.pieceDetailsTab}>
                <div
                    className={classes.pieceDetailsTabLeft}
                    style={{ height: expandPage ? '65vh' : '70vh' }}
                >
                    <div className={classes.pieceCardColumnHeader}>
                        {showPieceAsItem ? t('ITEMS_DETAILS') : t('PIECES_DETAILS')}
                        <span className={classes.piecesNumber}>{piecesArray.length}</span>
                    </div>
                    {piecesArray.map((piece: any, idx: number) => {
                        return renderPieceCard(piece, idx);
                    })}
                </div>
                <div
                    className={classes.pieceDetailsTabRight}
                    style={{ height: expandPage ? '65vh' : '70vh' }}
                >
                    <div className={classes.detailsTab}>
                        {renderPieceDetails(piecesArray[currentPieceNumber], currentPieceNumber)}
                    </div>
                </div>
            </div>
        );
    };

    const renderAddressDetailsTab = () => {
        const consignorDetailsAddressKeys = ['sender_address_line_1', 'sender_address_line_2', 'sender_address_line_3', 'sender_address_line_4', 'sender_address_line_5'];
        const consigneeDetailsAddressKeys = ['destination_address_line_1', 'destination_address_line_2', 'destination_address_line_3', 'destination_address_line_4', 'destination_address_line_5'];

        return (
            <div className={classes.detailsTab}>
                <div className={classes.addressDetails}>
                    <div className={classes.addressBox}>
                        <div className={classes.addressInfoUpper}>
                            <div className={classes.addressInfoUpperLeft}>
                                <span className={classes.addressType}>
                                    {'   '}
                                    <ContentFilledIcon style={{
                                        color: '#6A3DF5',
                                        backgroundColor: 'transparent',
                                    }}
                                    />
                                    {'   '}
                                    {t('CONSIGNOR_DETAILS')}
                                </span>
                                <div>
                                    <span className={classes.addressPincode}>
                                        {consignment.sender_pincode}
                                        {' '}
                                    </span>
                                    <span className={classes.addressCSC}>
                                        {consignment.sender_state ? `${consignment.sender_state}, ` : null}
                                        {consignment.sender_city ? `${consignment.sender_city}, ` : null}
                                        {consignment.sender_country ? `${consignment.sender_country}` : null}
                                    </span>
                                </div>
                            </div>
                            <div className={classes.addressInfoUpperRight}>
                                <span className={classes.addressName}>{consignment.sender_name}</span>
                                <span className={classes.addressContact}>{consignment.sender_phone}</span>
                            </div>
                        </div>
                        <div className={classes.horizontalSeperator} />
                        <div className={classes.addressInfoLower}>
                            {consignorDetailsAddressKeys.map((key) => (
                                renderField(key, '100%')
                            ))}
                            {consignment.sender_service_time_mins ? (
                                <div className={classes.addressServiceTimeBox}>
                                    <ClockCircleTwoTone />
                                    {' '}
                                    <span>
                                        {consignment.sender_service_time_mins}
                                        {' MINS'}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={classes.addressVerticalJoin} />
                    <div className={classes.verticalSpace} />
                    <div className={classes.addressVerticalJoin} />
                    <div className={classes.verticalSpace} />
                    <div className={classes.addressVerticalJoin} />

                    <div className={classes.addressBox}>
                        <div className={classes.addressInfoUpper}>
                            <div className={classes.addressInfoUpperLeft}>
                                <span className={classes.addressType}>
                                    {'   '}
                                    <EnvironmentFilled style={{
                                        color: '#5CAF7E',
                                    }}
                                    />
                                    {'   '}
                                    {t('CONSIGNEE_DETAILS')}
                                </span>
                                <div>
                                    <span className={classes.addressPincode}>
                                        {consignment.destination_pincode}
                                        {' '}
                                    </span>
                                    <span className={classes.addressCSC}>
                                        {consignment.destination_state ? `${consignment.destination_state}, ` : null}
                                        {consignment.destination_city ? `${consignment.destination_city}, ` : null}
                                        {consignment.destination_country ? `${consignment.destination_country}` : null}
                                    </span>
                                </div>
                            </div>
                            <div className={classes.addressInfoUpperRight}>
                                <span className={classes.addressName}>{consignment.destination_name}</span>
                                <span className={classes.addressContact}>{consignment.destination_phone}</span>
                            </div>
                        </div>
                        <div className={classes.horizontalSeperator} />
                        <div className={classes.addressInfoLower}>
                            {consigneeDetailsAddressKeys.map((key) => (
                                renderField(key, '100%')
                            ))}
                            {consignment.destination_service_time_mins ? (
                                <div className={classes.addressServiceTimeBox}>
                                    <ClockCircleTwoTone />
                                    {' '}
                                    <span>
                                        {consignment.destination_service_time_mins}
                                        {' MINS'}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDetails = () => {
        switch (selectedTab) {
            case 'primary_details':
                return (
                    <>
                        {renderPrimaryDetailsTab()}
                    </>
                );
            case 'pieces_details':
                return (
                    <>
                        {renderPiecesDetailsTab()}
                    </>
                );
            case 'address_details':
                return (
                    <>
                        {renderAddressDetailsTab()}
                    </>
                );
            default:
                return (<h3>Tab details not handled yet</h3>);
        }
    };

    const renderImagePreview = () => {
        if (!imageUrl) return null;
        return (
            <Image
                src={imageUrl}
                width={0}
                preview={{
                    visible: true,
                    src: imageUrl,
                    onVisibleChange: (value) => {
                        if (!value) setImageUrl(null);
                    },
                }}
            />
        );
    };

    return (
        <>
            <div className={classes.mainDiv}>
                {renderDetails()}
                {renderImagePreview()}
            </div>
            {isPriceDetailsModalVisible && (
                <TableModal
                    width={500}
                    header="Details"
                    isVisible={isPriceDetailsModalVisible}
                    handleModalClose={() => {
                        setIsPriceDetailsModalVisible(false);
                        setPriceDataList([]);
                    }}
                    dataSource={priceDataList}
                    columnsToShow={priceDetailsModalColumns}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { parts_to_show } = master;
    const separateChildCN = parts_to_show?.separate_child_cns;
    const currencySymbol = master?.config?.currency_symbol || '';
    const completeCustomFieldsList = master?.config?.complete_custom_fields_list || [];
    return {
        consignmentSectionList: state.genericPage?.[PAGE_NAMES.CONSIGNMENTS]?.metadata?.sectionList,
        separateChildCN,
        currencySymbol,
        completeCustomFieldsList,
        consignment_details_column_list: state.generic?.CONSIGNMENTS?.detailsColumns?.selectedColumns,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: consignmentDetailsIntStyles,
    },
    connectRouter: true,
};

export default GenericHoc(hocConfig)(ConsignmentDetailedInternational);
